/**
 * 「先来一条」两张大卡(2026-09-12 hub 三段式重排)—— 自由创作 / 真人出镜。
 *
 * ═══ 形制照抄视觉舱,不另造一套 ═══════════════════════════════════════════════
 * 这两张对视频舱的意义,与 `PosterFreeCard`(「先来一张」:我自己描述 / 拿一张图改)
 * 对视觉舱完全同构 —— **没有固定产物**(出什么全看用户写什么),所以:
 *   · 不套 `SceneCapCard`:那张卡的舞台放**真产物样图**(hover peek),这两张没有真样图,
 *     硬塞要么配一张假样图(破「有真出品才上卡」铁律),要么留一个空舞台。
 *   · 舞台改放**主角图**,其余(卡形 / 舞台比例 / 三层文案 / 分隔线 / 底栏价签+CTA)
 *     逐件复用同一套类:`CARD_WALL_RADIUS` + `.scene-stage` + `.scene-cta-arrow`。
 * 与视觉舱那两张唯一的差别是舞台左下角挂**形制角标**(「▸ 1 条视频」),与目的卡对齐 ——
 * 这一舱的产物形制是需要说的那一维(方图/长图那套在视觉舱才有歧义)。
 *
 * ★价签(B2 #9,0927 K4 实测):两张卡**各报各的路** —— 自由创作 = 直发(每秒价 × 5,不含编排与
 *   台本图,catalog `videoFreeClipCostLabel`);真人出镜 = 其默认档(满血 2.0 · 720P · 15s,
 *   `realPersonDefaultCostLabel`)。此前两张与 8 张目的卡共用「约 565(含 1 张台本图)」,直发实扣 475。
 *
 * TODO(主角图):视频舱没有自己的 `video_free_*` / `video_live_*` 主角图素材,
 *   先各借一张语义最近的(自由创作借视觉舱「我自己描述」那位、真人出镜借分身舱
 *   「照片数字人」那位)。重出一批视频舱专属主角图后,把 `figKey` 换成新文件名即可 ——
 *   缺图是静默降级(背景图 404 什么都不显示,卡其余部分照常),不会破版。
 */
import type { KeyboardEvent } from "react";
import { ArrowRight, PencilLine, UserSquare } from "lucide-react";

import { videoFreeClipCostLabel } from "@/entities/scene-card/catalog";
import { figureOf } from "@/entities/scene-card/model/figures";
import { SHAPE_META } from "@/entities/scene-card/model/shapes";
import { SceneShapeGlyph } from "@/entities/scene-card/ui/SceneShapeGlyph";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";

import { realPersonDefaultCostLabel } from "../../model/videoCardCost";

const VARIANTS = {
  free: {
    icon: PencilLine,
    /** 主角图 key —— `/scene-figures/<skin>/<figKey>.webp`(见头注 TODO:暂借视觉舱那位)。 */
    figKey: "poster_free_text",
    title: "自由创作",
    usage: "自己写,不套目的",
    // ★分流收口(2026-09-14):自由创作=直发单镜,编排话术(判怎么拍/粘分镜脚本)随编排出口一起撤下。
    desc: "一段文字直接生一段画面(5~30 秒单镜),写好口播直出。",
    tags: ["文字生片"],
    badge: null as string | null,
  },
  live: {
    icon: UserSquare,
    figKey: "dh_photo",
    title: "真人出镜",
    // 0924 车 D:旧文案「上传照片,让 TA 出演整条片 / 1~3 张正脸照,每一镜锁同一张脸」
    // 与现页面不符 —— 实际是从人物库选已建档出镜人、写文案单镜直出(可套样片脚本)。
    usage: "从人物库选出镜人,写文案直接出片",
    desc: "选一位已建档授权的出镜人,写一句想拍的画面或套一条样片脚本,单镜直出。",
    tags: ["人物库出镜", "需授权"],
    badge: "新",
  },
} as const;

export type VideoFreeCardVariant = keyof typeof VARIANTS;

export function VideoFreeCard({
  variant,
  onOpen,
}: {
  variant: VideoFreeCardVariant;
  onOpen: () => void;
}) {
  const v = VARIANTS[variant];
  const Icon = v.icon;
  const skin = useActiveSkin();
  const { book } = usePriceBook();
  const costLabel =
    variant === "free" ? videoFreeClipCostLabel(book) : realPersonDefaultCostLabel(book);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      data-video-free-card={variant}
      className={cn(
        "scene-cap group relative flex cursor-pointer flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS
      )}
    >
      {/* 「新」徽标复用「最常用」那一颗的形制与 token(标准皮肤下 --primary 就是紫)——
          写死一个紫色十六进制会在 cyber / comic 两皮肤下与全站配色脱节(踩过)。 */}
      {v.badge && (
        <span className="absolute right-2 top-2 z-40 rounded-full bg-primary px-2 py-0.5 text-2xs font-semibold text-primary-foreground">
          {v.badge}
        </span>
      )}

      {/* 舞台:人物铺满(素材四周烘的就是该皮肤 --card,所以底要用 bg-card) */}
      <div className="scene-stage relative w-full overflow-hidden bg-card">
        <span
          className="absolute inset-0 z-10 bg-cover bg-[position:center_14%] bg-no-repeat"
          style={{ backgroundImage: `url(${figureOf(skin, v.figKey)})` }}
          aria-hidden
        />
        {/* 形制角标:与目的卡同一句「1 条视频」,位置也同在舞台左下 */}
        <span className="scene-shape absolute bottom-2 left-2 z-30 inline-flex items-center gap-1.5 rounded-full bg-card/85 px-2 py-1 text-card-foreground backdrop-blur-sm">
          <SceneShapeGlyph shape="video" />
          <span className="text-2xs font-medium leading-none">{SHAPE_META.video.label}</span>
        </span>
      </div>

      <div className="relative z-30 flex flex-1 flex-col gap-1 p-3">
        {/* h3 与目的卡同级:读屏用户按标题在卡墙里跳转时,这两张不能掉出层级 */}
        <h3 className="flex items-center gap-1.5 text-sm font-[650] tracking-[-0.012em]">
          <Icon
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            strokeWidth={1.8}
            aria-hidden
          />
          {v.title}
        </h3>
        <div className="text-sm font-medium leading-snug text-foreground/[0.86]">{v.usage}</div>
        <p className="line-clamp-2 text-2xs leading-[1.62] text-muted-foreground">{v.desc}</p>
        <div className="mt-auto flex flex-wrap gap-1 pt-1.5">
          {v.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border/60 bg-background/50 px-1.5 py-0.5 text-2xs text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="relative z-30 flex items-center justify-between border-t border-border/60 px-3 py-2">
        {/* 价目表没到货就出骨架,**不许兜一个数**(同 SceneCapCard 的规矩) */}
        {costLabel ? (
          <span className="text-2xs text-muted-foreground">{costLabel}</span>
        ) : (
          <span className="h-3 w-24 animate-pulse rounded bg-muted/60" aria-label="积分标加载中" />
        )}
        <span className="inline-flex items-center gap-0.5 text-2xs font-semibold text-primary">
          立即创作
          <ArrowRight className="scene-cta-arrow h-3 w-3" />
        </span>
      </div>
    </div>
  );
}
